import { Organization } from 'web/model/Organization';
import { create } from 'zustand';
import Cookies from 'js-cookie';
import { useEffect } from 'react';

const useCurrentOrganizationStore = create<{
    currentOrganization: Organization | null,
    setCurrentOrganization: (org: Organization | null) => void,
    initializeOrganization: () => Organization | null,
}>()((set, get) => {
    const getCurrentOrganization = () => {
        const cookie = Cookies.get('nx-org');
        const orgData = (cookie && (window as any).INIT_USER?.organizations?.find((org: Organization) => org.id === cookie)) || (window as any).INIT_USER?.organizations?.[0];
        if (!orgData) {
            return null;
        }
        const org = new Organization(orgData);

        // Set the cookie if it doesn't exist or doesn't match the selected org
        if (!cookie || cookie !== org.id) {
            Cookies.set('nx-org', org.id);
        }

        set({ currentOrganization: org });
        return org;
    };

    const setCurrentOrganization = (org: Organization | null) => {
        if (!org) {
            Cookies.remove('nx-org'); 
        } else {
            Cookies.set('nx-org', org.id);
        }
        
        return set(() => ({
            currentOrganization: org,
        }));
    };

    const initializeOrganization = () => {
        const currentState = get();
        if (currentState.currentOrganization === null) {
            return getCurrentOrganization();
        }
        return currentState.currentOrganization;
    };

    return {
        currentOrganization: null, // Initialize as null, will be set when initializeOrganization is called
        setCurrentOrganization,
        initializeOrganization,
    }
});

// Main hook that handles lazy initialization
export const useCurrentOrganization = () => {
    const { currentOrganization, setCurrentOrganization, initializeOrganization } = useCurrentOrganizationStore();
    
    useEffect(() => {
        // Initialize the organization on first use
        if (currentOrganization === null) {
            initializeOrganization();
        }
    }, [currentOrganization, initializeOrganization]);
    
    return {
        currentOrganization,
        setCurrentOrganization,
    };
};
